import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Mail } from "lucide-react";

/**
 * One-time prompt that appears on login for admins who signed up BEFORE the
 * required-email migration. They must add their email so driver-portal emails
 * route replies to them. Dismisses as soon as the email is saved and never
 * re-shows for that user.
 */
export default function EmailBackfillModal({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role === "superadmin") return;        // super-admin uses a separate notifications flow
    if (user.role !== "admin") return;             // only admins own the workspace email
    // If the user object was hydrated from an older cache / older login response
    // that didn't include the `email` key at all, wait until /auth/me refreshes
    // the user with authoritative data — otherwise we'd flash this modal on every
    // login even for admins who already saved their email.
    if (!Object.prototype.hasOwnProperty.call(user, "email")) return;
    if ((user.email || "").trim()) return;         // already set
    if (localStorage.getItem(`email_backfill_skip_${user.user_id}`)) return;
    setOpen(true);
  }, [user]);

  const save = async () => {
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/set-admin-email", { email: v });
      toast.success("Email saved — drivers will now reply directly to you");
      setOpen(false);
      await onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not save email"));
    } finally {
      setSaving(false);
    }
  };

  const skipOnce = () => {
    // Soft skip — stored per-user so it doesn't annoy on every reload today,
    // but will show again if they log out + back in (nudging them to add it).
    localStorage.setItem(`email_backfill_skip_${user.user_id}`, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skipOnce()}>
      <DialogContent className="max-w-md" data-testid="email-backfill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={18} className="text-blue-600" /> One quick thing — your email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            We now use your email as the <strong>reply address</strong> on every message your drivers receive (portal invites, pay statements, receipt approvals). If a driver hits Reply, it comes straight to you &mdash; not to us.
          </p>
          <div>
            <Label>Your email address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="payroll@yourcompany.com"
              autoFocus
              data-testid="email-backfill-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={skipOnce} data-testid="email-backfill-skip">Later</Button>
          <Button
            onClick={save}
            disabled={saving || !email.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="email-backfill-save"
          >
            {saving ? "Saving…" : "Save email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
